/**
 * MSA + DPA Templates — one combined enterprise-procurement page.
 *
 * Two anchored sections (Master Services Agreement + Data Processing
 * Agreement) auto-filled with our legal entity (CARTIEAI LLC), Texas
 * governing law, liability caps, and the subprocessor pointer.
 *
 * Designed to be:
 *  - Sent as a single URL to a buyer's procurement / legal team
 *  - Printed to PDF via window.print() (print-stylesheet included)
 *  - Linked from /contact, /privacy-policy, /subprocessors, /terms
 *
 * NOT a substitute for legal review on either side. The page makes
 * that disclaimer visible on first paint.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  FileText, ShieldCheck, ArrowLeft, Printer, Download, Mail, Phone,
  ScrollText, Database, AlertTriangle, CheckCircle2, History, FileSignature,
} from 'lucide-react';

const TODAY = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

// Public revision history — append new entries here when MSA/DPA is amended.
// Visible at the bottom of the page; signals "this is a maintained living
// document" rather than a one-shot template.
const CHANGELOG = [
  {
    version: '1.0',
    date: 'June 6, 2026',
    type: 'Initial release',
    typeColor: 'emerald',
    notes: 'First public draft of CARTIEAI LLC MSA + DPA. Governing law: Texas, USA. Liability cap: 12 months of paid fees.',
    diff: null,
  },
];

const SECTION_CLS = 'scroll-mt-28 mb-16 print:mb-8';
const H2_CLS = 'text-2xl sm:text-3xl font-bold text-white mb-4 tracking-tight print:text-black print:text-xl';
const H3_CLS = 'text-lg font-semibold text-emerald-300 mt-7 mb-2 print:text-black print:mt-4';
const P_CLS = 'text-slate-300 leading-relaxed mb-3 text-[15px] print:text-black print:text-[12px]';
const UL_CLS = 'list-disc pl-6 space-y-1.5 text-slate-300 mb-3 text-[15px] print:text-black print:text-[12px]';
const STRONG_CLS = 'text-white font-semibold print:text-black';

export default function MsaDpaTemplates() {
  const [activeDoc, setActiveDoc] = useState('msa');

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="msa-dpa-page">
      <Helmet>
        <title>MSA + DPA Templates · CARTIEAI LLC</title>
        <meta
          name="description"
          content="Pre-signed Master Services Agreement and Data Processing Agreement templates from CARTIEAI LLC. Texas governing law, liability caps disclosed. Send to your procurement team."
        />
      </Helmet>

      {/* Ambient color washes — matches Contact / Landing aesthetic */}
      <div className="fixed top-0 left-1/4 w-[40rem] h-[40rem] -translate-y-1/2 bg-emerald-500/8 blur-[140px] rounded-full pointer-events-none print:hidden" />
      <div className="fixed bottom-0 right-1/4 w-[36rem] h-[36rem] translate-y-1/2 bg-violet-500/10 blur-[140px] rounded-full pointer-events-none print:hidden" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 print:px-0 print:py-0 print:max-w-full">

        {/* Top nav — hidden on print */}
        <div className="flex items-center justify-between mb-10 print:hidden">
          <Link
            to="/contact"
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-emerald-300 transition-colors"
            data-testid="msa-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Contact
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
              data-testid="msa-print-btn"
            >
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Save as PDF
            </Button>
            <a
              href="mailto:hello@cartieai.com?subject=MSA%20%2B%20DPA%20review%20%E2%80%94%20%5Byour%20company%5D&body=Hi%20CARTIEAI%20team%2C%0A%0AOur%20legal%20team%20has%20reviewed%20your%20MSA%20%2B%20DPA%20at%20cartieai.com%2Flegal%2Ftemplates%20and%20we%27d%20like%20to%20discuss%3A%0A%0A%20-%20%5Bredlines%2C%20if%20any%5D%0A%20-%20%5Bspecific%20clauses%20to%20negotiate%5D%0A%0AThanks."
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold transition-colors"
              data-testid="msa-email-cta"
            >
              <Mail className="w-3.5 h-3.5" /> Email legal team
            </a>
          </div>
        </div>

        {/* Header */}
        <div className="mb-10 border-b border-slate-800 pb-8 print:border-black print:pb-4">
          <Badge className="bg-violet-500/15 border border-violet-500/30 text-violet-200 text-[10px] font-mono uppercase tracking-widest mb-4 print:bg-transparent print:border-black print:text-black">
            <ScrollText className="w-3 h-3 mr-1.5 inline" />
            Enterprise procurement pack
          </Badge>
          <h1 className="text-3xl sm:text-5xl font-bold mb-3 tracking-tight print:text-black print:text-3xl">
            MSA <span className="text-emerald-300">+</span> DPA <span className="text-slate-500 font-normal">templates</span>
          </h1>
          <p className="text-base text-slate-400 max-w-2xl print:text-black">
            Our standard Master Services Agreement and Data Processing Agreement, pre-filled with{' '}
            <strong className="text-white print:text-black">CARTIEAI LLC</strong> (a Texas limited liability
            company). Designed to short-circuit the 4–6 week back-and-forth that most enterprise procurement
            calls start with. Send this URL to your legal team — they can redline directly and email us back.
          </p>

          <div className="flex flex-wrap gap-4 mt-5 text-xs text-slate-500 print:text-black">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" /> Version 1.0
            </span>
            <span>Drafted: {TODAY}</span>
            <span>Governing law: Texas, USA</span>
            <span>Entity: CARTIEAI LLC</span>
          </div>
        </div>

        {/* Disclaimer — shown above the docs */}
        <div className="mb-10 rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 flex items-start gap-3 print:bg-transparent print:border-black" data-testid="msa-disclaimer">
          <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5 print:text-black" />
          <div className="text-sm text-amber-100/90 print:text-black">
            <p className="font-semibold mb-1 text-amber-200 print:text-black">Not a substitute for legal review.</p>
            <p>
              These templates reflect our standard terms and are offered as a starting point in good faith.
              You are encouraged to have your own counsel review them before signing. We're happy to negotiate
              reasonable redlines — email <a href="mailto:hello@cartieai.com" className="underline text-amber-200 print:text-black">hello@cartieai.com</a>.
            </p>
          </div>
        </div>

        {/* Sticky doc-switcher tabs — hidden on print */}
        <div className="sticky top-4 z-30 mb-10 print:hidden">
          <div className="flex gap-2 p-1.5 rounded-xl bg-slate-900/80 backdrop-blur border border-white/10 shadow-2xl shadow-black/40 max-w-md mx-auto">
            <button
              onClick={() => {
                setActiveDoc('msa');
                document.getElementById('msa')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeDoc === 'msa'
                  ? 'bg-emerald-500 text-white shadow-[0_0_18px_-4px_rgba(16,185,129,0.6)]'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid="msa-tab-msa"
            >
              <FileText className="w-3.5 h-3.5 inline mr-1.5" /> Master Services Agreement
            </button>
            <button
              onClick={() => {
                setActiveDoc('dpa');
                document.getElementById('dpa')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeDoc === 'dpa'
                  ? 'bg-violet-500 text-white shadow-[0_0_18px_-4px_rgba(139,92,246,0.6)]'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid="msa-tab-dpa"
            >
              <Database className="w-3.5 h-3.5 inline mr-1.5" /> Data Processing Agreement
            </button>
          </div>
        </div>

        {/* === MSA === */}
        <section id="msa" className={SECTION_CLS} data-testid="msa-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 ring-1 ring-white/20 flex items-center justify-center shadow-[0_0_24px_0_rgba(16,185,129,0.55)] print:bg-transparent print:shadow-none">
              <FileText className="w-6 h-6 text-white print:text-black" strokeWidth={2.25} />
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300 print:text-black">Document 1 of 2</p>
              <h2 className="text-2xl sm:text-4xl font-bold text-white print:text-black">Master Services Agreement</h2>
            </div>
          </div>

          <p className={P_CLS}>
            This Master Services Agreement (the "<span className={STRONG_CLS}>Agreement</span>") is made and
            entered into as of the date of the last signature below (the "<span className={STRONG_CLS}>Effective Date</span>")
            by and between <span className={STRONG_CLS}>CARTIEAI LLC</span>, a Texas limited liability company with its
            principal place of business in Texas, United States ("<span className={STRONG_CLS}>CARTIE&nbsp;AI</span>")
            and the customer entity signing below ("<span className={STRONG_CLS}>Customer</span>").
          </p>

          <h3 className={H3_CLS}>1. Services</h3>
          <p className={P_CLS}>
            CARTIEAI will provide Customer with access to its AI-native cloud financial intelligence platform
            and related services (the "<span className={STRONG_CLS}>Services</span>"), as further described at
            <a href="https://cartieai.com" className="text-emerald-300 underline ml-1 print:text-black">cartieai.com</a> and
            in any mutually agreed Order Form or Statement of Work. The Services are subject to our publicly
            posted <Link to="/terms" className="text-emerald-300 underline print:text-black">Terms of Service</Link>,
            which are incorporated by reference. In case of conflict between this Agreement and the public Terms,
            this Agreement controls for the Customer.
          </p>

          <h3 className={H3_CLS}>2. Fees and Payment</h3>
          <ul className={UL_CLS}>
            <li>Fees are set out in the applicable Order Form, invoiced monthly or annually via Stripe.</li>
            <li>Net 30 payment terms from invoice date. Late payments accrue interest at 1% per month.</li>
            <li>Fees exclude applicable taxes; Customer is responsible for all sales, VAT, or similar levies.</li>
            <li>Fees may be increased at renewal with at least 30 days' written notice.</li>
          </ul>

          <h3 className={H3_CLS}>3. Term and Termination</h3>
          <ul className={UL_CLS}>
            <li><span className={STRONG_CLS}>Initial term:</span> as specified on the Order Form (typically 12 months), automatically renewing for successive 12-month periods unless either party gives 30 days' written notice of non-renewal.</li>
            <li><span className={STRONG_CLS}>Termination for cause:</span> either party may terminate immediately upon a material breach by the other party that remains uncured 30 days after written notice.</li>
            <li><span className={STRONG_CLS}>Termination for convenience:</span> Customer may terminate at any time; pre-paid fees are non-refundable except as expressly stated in our <Link to="/refund" className="text-emerald-300 underline print:text-black">Refund Policy</Link>.</li>
            <li><span className={STRONG_CLS}>Effect of termination:</span> Customer may export data for 30 days; thereafter Customer data is permanently deleted (except where retention is legally required).</li>
          </ul>

          <h3 className={H3_CLS}>4. Confidentiality</h3>
          <p className={P_CLS}>
            Each party agrees to protect the other party's Confidential Information with at least the same degree
            of care it uses for its own confidential information of like importance, and never less than reasonable
            care. Confidential Information does not include information that is publicly available, independently
            developed, or rightfully received from a third party without restriction.
          </p>

          <h3 className={H3_CLS}>5. Data Ownership and License</h3>
          <p className={P_CLS}>
            <span className={STRONG_CLS}>Customer retains ownership</span> of all data it provides to or generates
            through the Services ("<span className={STRONG_CLS}>Customer Data</span>"). Customer grants CARTIEAI
            a limited, non-exclusive, worldwide license to host, process, and display Customer Data solely to
            provide the Services. CARTIEAI will not use Customer Data to train AI models for the benefit of
            other customers. Aggregated, de-identified statistics may be used to improve the Services.
          </p>

          <h3 className={H3_CLS}>6. Security and Privacy</h3>
          <p className={P_CLS}>
            CARTIEAI will maintain administrative, physical, and technical safeguards designed to protect the
            security, confidentiality, and integrity of Customer Data, as described in our{' '}
            <Link to="/privacy-policy" className="text-emerald-300 underline print:text-black">Privacy Policy</Link>{' '}
            and the Data Processing Agreement below. Current safeguards include AES-256 encryption at rest,
            TLS 1.3 in transit, bcrypt password hashing, RBAC, audit logging, and 35-day encrypted backups.
            Formal SOC 2 Type II audit is targeted for Q2 2026.
          </p>

          <h3 className={H3_CLS}>7. Warranties</h3>
          <p className={P_CLS}>
            CARTIEAI warrants that the Services will perform materially as described in our public documentation
            and the applicable Order Form. <span className={STRONG_CLS}>EXCEPT AS EXPRESSLY SET FORTH HEREIN, THE
            SERVICES ARE PROVIDED "AS IS" AND CARTIEAI DISCLAIMS ALL OTHER WARRANTIES, EXPRESS OR IMPLIED,
            INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.</span>
          </p>

          <h3 className={H3_CLS}>8. Indemnification</h3>
          <p className={P_CLS}>
            CARTIEAI will defend Customer against any third-party claim that the Services infringe a U.S. patent,
            copyright, or trade secret, and will indemnify Customer for damages finally awarded, provided Customer
            promptly notifies CARTIEAI in writing and allows CARTIEAI sole control of the defense. Customer will
            similarly defend CARTIEAI against claims arising from Customer Data or Customer's use of the Services
            in violation of this Agreement.
          </p>

          <h3 className={H3_CLS}>9. Limitation of Liability</h3>
          <p className={P_CLS}>
            <span className={STRONG_CLS}>TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL EITHER PARTY BE
            LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST
            PROFITS OR REVENUES.</span> Each party's aggregate liability under this Agreement will not exceed
            the fees paid or payable by Customer in the 12 months preceding the claim. The foregoing limitations
            do not apply to (a) breaches of confidentiality, (b) indemnification obligations, or (c) Customer's
            payment obligations.
          </p>

          <h3 className={H3_CLS}>10. Governing Law and Disputes</h3>
          <p className={P_CLS}>
            This Agreement is governed by the laws of the <span className={STRONG_CLS}>State of Texas, United States</span>,
            without regard to conflict-of-law rules. The state and federal courts located in{' '}
            <span className={STRONG_CLS}>Travis County, Texas</span> have exclusive jurisdiction over any dispute,
            except that either party may seek injunctive relief in any court of competent jurisdiction. Parties
            agree to attempt good-faith resolution within 30 days before initiating litigation.
          </p>

          <h3 className={H3_CLS}>11. Miscellaneous</h3>
          <ul className={UL_CLS}>
            <li><span className={STRONG_CLS}>Notices:</span> All notices to CARTIEAI go to <a href="mailto:hello@cartieai.com" className="text-emerald-300 underline print:text-black">hello@cartieai.com</a>; notices to Customer go to the email on file.</li>
            <li><span className={STRONG_CLS}>Assignment:</span> Neither party may assign this Agreement without the other's prior written consent, except in connection with a merger, acquisition, or sale of substantially all assets.</li>
            <li><span className={STRONG_CLS}>Entire agreement:</span> This Agreement, together with any Order Form and the incorporated DPA below, constitutes the entire agreement between the parties.</li>
            <li><span className={STRONG_CLS}>Severability:</span> If any provision is held unenforceable, the remainder remains in effect.</li>
          </ul>

          {/* Signature block — print-friendly */}
          <div className="mt-10 grid sm:grid-cols-2 gap-6 print:gap-3">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 print:bg-transparent print:border-black">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-3 print:text-black">CARTIEAI LLC</p>
              <p className="text-sm text-slate-300 mb-1 print:text-black">By: <span className="text-white print:text-black">Lakshmi Kiranmai Guduru</span></p>
              <p className="text-xs text-slate-500 mb-4 print:text-black">Founder &amp; Manager</p>
              <div className="border-b border-slate-700 h-8 mb-1 print:border-black" />
              <p className="text-[10px] text-slate-500 print:text-black">Signature &amp; date</p>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/50 p-5 print:bg-transparent print:border-black">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 mb-3 print:text-black">CUSTOMER</p>
              <p className="text-sm text-slate-300 mb-1 print:text-black">By: <span className="border-b border-slate-700 inline-block min-w-[180px] print:border-black">&nbsp;</span></p>
              <p className="text-xs text-slate-500 mb-4 print:text-black">Title: <span className="border-b border-slate-700 inline-block min-w-[180px] print:border-black">&nbsp;</span></p>
              <div className="border-b border-slate-700 h-8 mb-1 print:border-black" />
              <p className="text-[10px] text-slate-500 print:text-black">Signature &amp; date</p>
            </div>
          </div>
        </section>

        {/* === DPA === */}
        <div className="print:break-before-page" />
        <section id="dpa" className={SECTION_CLS} data-testid="dpa-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-violet-500 ring-1 ring-white/20 flex items-center justify-center shadow-[0_0_24px_0_rgba(139,92,246,0.55)] print:bg-transparent print:shadow-none">
              <Database className="w-6 h-6 text-white print:text-black" strokeWidth={2.25} />
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-violet-300 print:text-black">Document 2 of 2</p>
              <h2 className="text-2xl sm:text-4xl font-bold text-white print:text-black">Data Processing Agreement</h2>
            </div>
          </div>

          <p className={P_CLS}>
            This Data Processing Agreement (the "<span className={STRONG_CLS}>DPA</span>") supplements the Master
            Services Agreement above between <span className={STRONG_CLS}>CARTIEAI LLC</span> (the "<span className={STRONG_CLS}>Processor</span>")
            and the Customer (the "<span className={STRONG_CLS}>Controller</span>"). It applies to all Personal Data
            (as defined by the EU General Data Protection Regulation 2016/679 and the California Consumer Privacy
            Act, collectively "<span className={STRONG_CLS}>Applicable Data Protection Laws</span>") that Processor
            processes on Controller's behalf.
          </p>

          <h3 className={H3_CLS}>1. Subject Matter and Duration</h3>
          <p className={P_CLS}>
            Processor will process Personal Data only for the duration of the underlying MSA and only as necessary
            to provide the Services. Categories of data subjects: Controller's employees, contractors, and
            end-users to the extent reflected in cloud cost and usage telemetry.
          </p>

          <h3 className={H3_CLS}>2. Nature and Purpose of Processing</h3>
          <p className={P_CLS}>
            Processor will process Personal Data solely to (a) provide the Services, (b) detect and prevent fraud
            or abuse, (c) comply with legal obligations, and (d) generate aggregated, de-identified statistics.
          </p>

          <h3 className={H3_CLS}>3. Categories of Personal Data</h3>
          <ul className={UL_CLS}>
            <li>Account-holder identifiers (name, work email, role)</li>
            <li>Authentication metadata (hashed password, MFA tokens, session timestamps)</li>
            <li>Usage telemetry (IP address, browser, pages visited)</li>
            <li>Cloud billing data ingested via read-only APIs (resource IDs, tags, costs)</li>
            <li>Customer-list metadata if Stripe integration is enabled (customer email, name, subscription state — no card data)</li>
          </ul>

          <h3 className={H3_CLS}>4. Processor Obligations</h3>
          <ul className={UL_CLS}>
            <li>Process Personal Data only on documented instructions from Controller.</li>
            <li>Ensure personnel are bound by confidentiality obligations.</li>
            <li>Implement appropriate technical and organisational measures (Section 7).</li>
            <li>Assist Controller in responding to data-subject rights requests and DPIAs.</li>
            <li>Notify Controller without undue delay (within 72 hours) of any Personal Data Breach.</li>
            <li>Delete or return all Personal Data within 30 days of termination, except where retention is legally required.</li>
          </ul>

          <h3 className={H3_CLS}>5. Subprocessors</h3>
          <p className={P_CLS}>
            Controller authorises Processor to engage subprocessors listed at{' '}
            <Link to="/subprocessors" className="text-violet-300 underline print:text-black">cartieai.com/subprocessors</Link>{' '}
            (current vendors include AWS, Stripe, Resend, MongoDB Atlas, Anthropic, OpenAI). Processor will notify
            Controller at least 14 days before engaging any new subprocessor and will impose contractual data-protection
            obligations no less stringent than those in this DPA.
          </p>

          <h3 className={H3_CLS}>6. International Transfers</h3>
          <p className={P_CLS}>
            Processor's primary data hosting is in the United States (MongoDB Atlas, US-East region). For Personal
            Data originating in the EU/UK, the parties incorporate the EU Standard Contractual Clauses (Module 2:
            controller-to-processor) by reference. Processor will assist Controller with any transfer-impact assessment.
          </p>

          <h3 className={H3_CLS}>7. Security Measures</h3>
          <ul className={UL_CLS}>
            <li>AES-256 encryption at rest; TLS 1.3 in transit</li>
            <li>Bcrypt password hashing; optional TOTP and email-based MFA</li>
            <li>Strict per-tenant isolation enforced at the database-collection level (automated tests on every release)</li>
            <li>Role-based access control with least-privilege defaults</li>
            <li>Audit logs of every privileged action, retained for 12 months</li>
            <li>35-day encrypted backups, then permanently destroyed</li>
            <li>Annual penetration testing (post-SOC 2 Type II); quarterly internal security audits today</li>
            <li>SOC 2 Type II audit targeted for Q2 2026</li>
          </ul>

          <h3 className={H3_CLS}>8. Audit Rights</h3>
          <p className={P_CLS}>
            Processor will, on reasonable notice and no more than once per 12-month period, make available to
            Controller (or its independent auditor under NDA) the information necessary to demonstrate compliance
            with this DPA, including the most recent SOC 2 report once available. Audits will be conducted during
            business hours and will not unreasonably interfere with Processor's operations.
          </p>

          <h3 className={H3_CLS}>9. Data Subject Rights</h3>
          <p className={P_CLS}>
            Processor will provide reasonable assistance, including by appropriate technical and organisational
            measures, to enable Controller to respond to requests from data subjects exercising rights under
            Applicable Data Protection Laws (access, rectification, erasure, portability, restriction, objection).
            Requests received directly by Processor will be forwarded to Controller within 5 business days.
          </p>

          <h3 className={H3_CLS}>10. Liability and Indemnification</h3>
          <p className={P_CLS}>
            Liability under this DPA is governed by Section 9 (Limitation of Liability) of the MSA. Each party
            indemnifies the other for fines or damages arising from its own breach of Applicable Data Protection
            Laws.
          </p>

          <h3 className={H3_CLS}>11. Governing Law</h3>
          <p className={P_CLS}>
            This DPA is governed by the laws of the State of Texas, United States. Where Applicable Data
            Protection Laws require, mandatory provisions of those laws prevail.
          </p>

          {/* DPA Signature block */}
          <div className="mt-10 grid sm:grid-cols-2 gap-6 print:gap-3">
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-5 print:bg-transparent print:border-black">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-violet-300 mb-3 print:text-black">PROCESSOR — CARTIEAI LLC</p>
              <p className="text-sm text-slate-300 mb-1 print:text-black">By: <span className="text-white print:text-black">Lakshmi Kiranmai Guduru</span></p>
              <p className="text-xs text-slate-500 mb-4 print:text-black">Founder &amp; Manager · Data Protection Lead</p>
              <div className="border-b border-slate-700 h-8 mb-1 print:border-black" />
              <p className="text-[10px] text-slate-500 print:text-black">Signature &amp; date</p>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/50 p-5 print:bg-transparent print:border-black">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 mb-3 print:text-black">CONTROLLER</p>
              <p className="text-sm text-slate-300 mb-1 print:text-black">By: <span className="border-b border-slate-700 inline-block min-w-[180px] print:border-black">&nbsp;</span></p>
              <p className="text-xs text-slate-500 mb-4 print:text-black">Title: <span className="border-b border-slate-700 inline-block min-w-[180px] print:border-black">&nbsp;</span></p>
              <div className="border-b border-slate-700 h-8 mb-1 print:border-black" />
              <p className="text-[10px] text-slate-500 print:text-black">Signature &amp; date</p>
            </div>
          </div>
        </section>

        {/* CTA strip — hidden on print */}
        <div className="mt-16 rounded-2xl border border-emerald-500/30 bg-slate-900 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-violet-500/10 p-8 print:hidden" data-testid="msa-bottom-cta">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle2 className="w-6 h-6 text-emerald-300" />
            <h3 className="text-xl font-bold text-white">Ready to redline?</h3>
          </div>
          <p className="text-slate-300 mb-5 text-sm max-w-2xl">
            Send this URL to your legal team. They can paste redlines directly into an email reply and we'll
            turn around v2 within 2 business days. Or hop on a 30-min call with the founder to walk through
            any concerns — most enterprise deals close in under a week.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="mailto:hello@cartieai.com?subject=MSA%20%2B%20DPA%20redlines%20%E2%80%94%20%5Byour%20company%5D"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold shadow-[0_0_24px_-4px_rgba(16,185,129,0.6)] transition-all"
              data-testid="msa-redline-cta"
            >
              <Mail className="w-4 h-4" /> Email redlines to legal
            </a>
            <a
              href="tel:+15126217610"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 text-sm font-semibold transition-all"
              data-testid="msa-call-cta"
            >
              <Phone className="w-4 h-4" /> +1 (512) 621-7610
            </a>
            <Button
              variant="outline"
              size="default"
              onClick={handlePrint}
              className="border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20"
              data-testid="msa-print-bottom"
            >
              <Download className="w-4 h-4 mr-2" /> Save as PDF
            </Button>
          </div>
        </div>

        {/* === Request signed copy form === */}
        <div className="mt-12 print:hidden" data-testid="msa-signed-copy-form">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-emerald-500 ring-1 ring-white/20 flex items-center justify-center shadow-[0_0_20px_0_rgba(16,185,129,0.55)]">
              <FileSignature className="w-5 h-5 text-white" strokeWidth={2.25} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Request a signed copy</h3>
              <p className="text-xs text-slate-400 mt-0.5">Fill this once. We'll send back a counter-signed PDF within 1 business day.</p>
            </div>
          </div>

          <RequestSignedCopyForm />
        </div>

        {/* === Public Changelog === */}
        <div className="mt-14 print:break-before-page" data-testid="msa-changelog">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-slate-800 ring-1 ring-white/10 flex items-center justify-center print:bg-transparent">
              <History className="w-5 h-5 text-slate-300 print:text-black" strokeWidth={2.25} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white print:text-black">Revision history</h3>
              <p className="text-xs text-slate-400 mt-0.5 print:text-black">Every change to this MSA + DPA is logged here. Public, timestamped, append-only.</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden print:border-black print:bg-transparent">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80 border-b border-slate-800 print:bg-transparent print:border-black">
                <tr className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-400 print:text-black">
                  <th className="text-left px-4 py-3">Version</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 print:divide-black">
                {CHANGELOG.map((entry) => (
                  <tr key={entry.version} className="hover:bg-slate-900/60 transition-colors" data-testid={`changelog-row-${entry.version}`}>
                    <td className="px-4 py-3 font-mono text-emerald-300 font-semibold print:text-black">v{entry.version}</td>
                    <td className="px-4 py-3 text-slate-300 print:text-black">{entry.date}</td>
                    <td className="px-4 py-3">
                      <Badge
                        className={`text-[10px] font-mono uppercase tracking-wider border print:bg-transparent print:border-black print:text-black ${
                          entry.typeColor === 'emerald'
                            ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200'
                            : entry.typeColor === 'amber'
                            ? 'bg-amber-500/15 border-amber-500/30 text-amber-200'
                            : 'bg-slate-700/40 border-slate-600 text-slate-300'
                        }`}
                      >
                        {entry.type}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs hidden sm:table-cell max-w-md print:text-black">{entry.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-slate-500 mt-3 print:text-black">
            We don't silently rewrite our standard terms. Every customer on a previous version keeps their original
            agreement until they explicitly renew under a newer version. <span className="text-slate-400 print:text-black">Amendments = 0.</span>
          </p>
        </div>
      </div>
      <style>{`
        @media print {
          body, html { background: white !important; }
          a { color: black !important; text-decoration: underline; }
          .print\\:break-before-page { break-before: page; page-break-before: always; }
          @page { margin: 1.2cm; }
        }
      `}</style>
    </div>
  );
}

/**
 * Request signed-copy mini-form — collects company info + redlines and
 * opens the user's mail client with a pre-filled, well-structured email
 * to hello@cartieai.com. Zero backend dependency (works immediately, no
 * spam risk, no rate-limiting needed). Local state only.
 */
function RequestSignedCopyForm() {
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    dealSize: '',
    redlines: '',
  });

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Signed MSA + DPA request — ${form.company || form.name}`);
    const body = encodeURIComponent(
      `Hi CARTIEAI team,\n\n` +
      `Please send a counter-signed copy of the MSA + DPA.\n\n` +
      `--- Customer details ---\n` +
      `Name: ${form.name}\n` +
      `Company: ${form.company}\n` +
      `Email: ${form.email}\n` +
      `Estimated deal size: ${form.dealSize || 'TBD'}\n\n` +
      `--- Redlines or requests ---\n` +
      `${form.redlines || '(none — accept your standard terms as-is)'}\n\n` +
      `Thanks.`
    );
    window.location.href = `mailto:hello@cartieai.com?subject=${subject}&body=${body}`;
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-6 sm:p-8 space-y-5" data-testid="signed-copy-form-card">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-1.5 block">Your name *</span>
          <input
            type="text"
            required
            value={form.name}
            onChange={update('name')}
            placeholder="Jane Doe"
            className="w-full h-11 px-3 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            data-testid="signed-name-input"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-1.5 block">Company *</span>
          <input
            type="text"
            required
            value={form.company}
            onChange={update('company')}
            placeholder="Acme Corp"
            className="w-full h-11 px-3 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            data-testid="signed-company-input"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-1.5 block">Work email *</span>
          <input
            type="email"
            required
            value={form.email}
            onChange={update('email')}
            placeholder="jane@acme.com"
            className="w-full h-11 px-3 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            data-testid="signed-email-input"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-1.5 block">Annual deal size</span>
          <select
            value={form.dealSize}
            onChange={update('dealSize')}
            className="w-full h-11 px-3 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            data-testid="signed-dealsize-select"
          >
            <option value="">Choose…</option>
            <option value="<$10k">Under $10k</option>
            <option value="$10k-$50k">$10k – $50k</option>
            <option value="$50k-$250k">$50k – $250k</option>
            <option value="$250k-$1M">$250k – $1M</option>
            <option value=">$1M">Over $1M</option>
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-1.5 block">Redlines or requests (optional)</span>
        <textarea
          rows={4}
          value={form.redlines}
          onChange={update('redlines')}
          placeholder={'e.g. "Cap liability at 6 months instead of 12" or "Add MNDA before exchanging redlines"'}
          className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 resize-none"
          data-testid="signed-redlines-input"
        />
        <span className="text-[11px] text-slate-500 mt-1.5 block">
          Leave blank to accept our standard terms as-is — fastest path to signature (typically &lt; 24 hours).
        </span>
      </label>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
        <button
          type="submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold shadow-[0_0_24px_-4px_rgba(16,185,129,0.6)] transition-all"
          data-testid="signed-copy-submit"
        >
          <FileSignature className="w-4 h-4" /> Request signed copy
        </button>
        <p className="text-[11px] text-slate-500">
          Opens your mail client with a pre-filled message to <span className="font-mono text-emerald-300">hello@cartieai.com</span>. No data is sent to us until you hit send.
        </p>
      </div>
    </form>
  );
}
